const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8090';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('authToken');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    
    return headers;
  }

  async handleResponse(response) {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'API request failed');
    }
    
    return data;
  }

  // Auth endpoints
  async register(email, password, username) {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email, password, username }),
    });
    
    const data = await this.handleResponse(response);
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async login(email, password) {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ email, password }),
    });
    
    const data = await this.handleResponse(response);
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async getCurrentUser() {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  async verifyEmail() {
    const response = await fetch(`${API_URL}/api/auth/verify-email`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  // User endpoints
  async getUserProfile(username) {
    const response = await fetch(`${API_URL}/api/users/${username}`, {
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  async updateProfile(data) {
    const response = await fetch(`${API_URL}/api/users/profile`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    
    return this.handleResponse(response);
  }

  async uploadProfilePicture(file) {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch(`${API_URL}/api/users/profile-picture`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });
    
    return this.handleResponse(response);
  }

  async uploadBackgroundImage(file) {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch(`${API_URL}/api/users/background-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });
    
    return this.handleResponse(response);
  }

  async followUser(username) {
    const response = await fetch(`${API_URL}/api/users/${username}/follow`, {
      method: 'POST',
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  async checkFollowing(username) {
    const response = await fetch(`${API_URL}/api/users/${username}/is-following`, {
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  async searchUsers(query) {
    const params = new URLSearchParams({ q: query });
    const response = await fetch(`${API_URL}/api/search/users?${params}`, {
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  // Card endpoints
  async saveCard(cardData) {
    const response = await fetch(`${API_URL}/api/cards/save`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(cardData),
    });
    
    return this.handleResponse(response);
  }

  async getMyCards(category = 'all', page = 1) {
    const params = new URLSearchParams({ category, page });
    const response = await fetch(`${API_URL}/api/cards/my-cards?${params}`, {
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  async getUserCards(username, category = 'all', page = 1) {
    const params = new URLSearchParams({ category, page });
    const response = await fetch(`${API_URL}/api/cards/user/${username}?${params}`, {
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  async getCard(cardId) {
    const response = await fetch(`${API_URL}/api/cards/${cardId}`, {
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  async deleteCard(cardId) {
    const response = await fetch(`${API_URL}/api/cards/${cardId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  async getCardStats() {
    const response = await fetch(`${API_URL}/api/cards/stats/overview`, {
      headers: this.getHeaders(),
    });
    
    return this.handleResponse(response);
  }

  // Card analysis (existing endpoint)
  async analyzeCard(imageData) {
    const response = await fetch(`${API_URL}/api/analyze-card`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ image: imageData }),
    });
    
    return this.handleResponse(response);
  }

  // Utility
  logout() {
    this.setToken(null);
  }

  getImageUrl(path) {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  }
}

const apiService = new ApiService();
export default apiService;